import { useState, useCallback } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { SettingsEmpty } from './SettingsPanel';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { useGradingTemplatesQuery } from './useGradingTemplatesQuery';
import { useDeleteGradingTemplate } from './useGradingTemplateMutations';
import GradingTemplateDialog from './GradingTemplateDialog';

interface Props {
  schoolId: string;
  /** Lets the overview's template count follow an add or a delete. */
  onChanged?: () => void;
}

export default function GradingTemplatesEditor({ schoolId, onChanged }: Props) {
  const { data, loading, refetch } = useGradingTemplatesQuery(schoolId);
  const [deleteTemplate] = useDeleteGradingTemplate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);

  const templates = (data?.school?.gradingTemplates ?? []) as ReadonlyArray<any>;

  const handleEdit = useCallback((t: any) => {
    setEditing(t);
    setDialogOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setEditing(null);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    await deleteTemplate({ variables: { input: { id: deleteConfirm.id } } });
    setDeleteConfirm(null);
    await refetch();
    onChanged?.();
  }, [deleteConfirm, deleteTemplate, refetch, onChanged]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Add template
        </Button>
      </div>

      {loading && templates.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground">Loading templates…</p>
      ) : templates.length === 0 ? (
        <SettingsEmpty
          action={
            <Button onClick={handleCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              Add template
            </Button>
          }
        >
          No templates yet. Add one for each way this school grades a subject — a standard
          high-school split, a humanities split, pass-fail.
        </SettingsEmpty>
      ) : (
        <ul className="divide-y rounded-lg border">
          {templates.map((t: any) => {
            // Weights that miss 100% still calculate, but almost never on purpose,
            // so the one template that is off is the thing worth seeing here.
            const balanced = Math.round(Number(t.weightTotal)) === 100;
            return (
              <li key={t.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium">{t.name}</p>
                    {t.description && (
                      <p className="mt-0.5 text-sm text-muted-foreground">{t.description}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <span
                      className={`mr-1 text-sm tabular-nums ${
                        balanced ? 'text-muted-foreground' : 'font-medium text-[--settings-attention]'
                      }`}
                    >
                      {Number(t.weightTotal).toFixed(0)}%
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(t)}
                      aria-label={`Edit ${t.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteConfirm(t)}
                      aria-label={`Delete ${t.name}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[...t.categories]
                    .sort((a: any, b: any) => a.position - b.position)
                    .map((c: any) => (
                      <Badge key={c.id} variant="outline" className="font-normal">
                        {c.name} {Number(c.weight).toFixed(0)}%
                        {c.dropLowest > 0 && ` · drop ${c.dropLowest}`}
                      </Badge>
                    ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <GradingTemplateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        schoolId={schoolId}
        template={editing}
        onComplete={() => {
          setDialogOpen(false);
          refetch();
          onChanged?.();
        }}
      />

      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteConfirm?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Classes that already applied this template will keep their categories. The template will just no longer appear in the picker.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
